import React, { useState, useEffect, useCallback } from 'react';
import { Radio, Copy, Check, RefreshCw, Play, Square, Trash2, Plus, Link2, Unlink, Clock, AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  active: 'bg-red-500',
  idle: 'bg-yellow-500',
  disabled: 'bg-gray-500',
};

const AdminChannels = ({ games = [], onRefresh }) => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [copiedField, setCopiedField] = useState(null);

  // Assign dialog
  const [assignDialog, setAssignDialog] = useState(null); // { channelId, channelName }
  const [selectedGameId, setSelectedGameId] = useState('');

  // Conflict dialog
  const [conflictDialog, setConflictDialog] = useState(null); // { channelId, gameId, conflict }

  // Schedule stop dialog
  const [scheduleDialog, setScheduleDialog] = useState(null); // channelId
  const [scheduleTime, setScheduleTime] = useState('');

  const loadChannels = useCallback(async () => {
    try {
      const res = await api.getChannels();
      setChannels(res.data);
    } catch (e) {
      toast.error('Failed to load channels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  const copyText = async (text, field) => {
    try { await navigator.clipboard.writeText(text); } catch {
      const i = document.createElement('input'); i.value = text; document.body.appendChild(i); i.select(); document.execCommand('copy'); document.body.removeChild(i);
    }
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const createChannel = async () => {
    if (!newName.trim()) return toast.error('Enter a channel name');
    setCreating(true);
    try {
      await api.createChannel({ name: newName.trim() });
      setNewName('');
      toast.success('Channel created');
      loadChannels();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create channel');
    } finally {
      setCreating(false);
    }
  };

  const deleteChannel = async (id, name) => {
    if (!window.confirm(`Delete channel "${name}"? This will also delete the Mux live stream.`)) return;
    try {
      await api.deleteChannel(id);
      toast.success('Channel deleted');
      loadChannels();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const refreshStatus = async (id) => {
    try {
      await api.getChannelStatus(id);
      loadChannels();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to refresh status');
    }
  };

  const enableChannel = async (id) => {
    try {
      await api.enableChannel(id);
      toast.success('Channel enabled — ready for RTMP ingest');
      loadChannels();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to enable');
    }
  };

  const disableChannel = async (id) => {
    try {
      await api.disableChannel(id);
      toast.success('Channel stopped');
      loadChannels();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to disable');
    }
  };

  const unassignChannel = async (id) => {
    if (!window.confirm('Unassign this channel from its current game?')) return;
    try {
      await api.unassignChannel(id);
      toast.success('Channel unassigned');
      loadChannels();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to unassign');
    }
  };

  const handleAssign = async () => {
    if (!selectedGameId || !assignDialog) return;
    try {
      const res = await api.assignChannel(assignDialog.channelId, { game_id: selectedGameId });
      if (res.data.conflict) {
        setConflictDialog({
          channelId: assignDialog.channelId,
          gameId: selectedGameId,
          conflict: res.data.conflict,
        });
        setAssignDialog(null);
      } else {
        toast.success('Channel assigned to game');
        setAssignDialog(null);
        setSelectedGameId('');
        loadChannels();
        onRefresh?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign');
    }
  };

  const handleForceAssign = async () => {
    if (!conflictDialog) return;
    try {
      await api.forceAssignChannel(conflictDialog.channelId, { game_id: conflictDialog.gameId });
      toast.success('Channel reassigned (previous game marked as finished)');
      setConflictDialog(null);
      setSelectedGameId('');
      loadChannels();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to force assign');
    }
  };

  const handleScheduleStop = async () => {
    if (!scheduleDialog || !scheduleTime) return;
    try {
      const dt = new Date(scheduleTime);
      await api.scheduleChannelStop(scheduleDialog, { stop_at: dt.toISOString() });
      toast.success(`Scheduled stop at ${dt.toLocaleString()}`);
      setScheduleDialog(null);
      setScheduleTime('');
      loadChannels();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to schedule stop');
    }
  };

  const cancelSchedule = async (id) => {
    try {
      await api.cancelChannelSchedule(id);
      toast.success('Scheduled stop cancelled');
      loadChannels();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel schedule');
    }
  };

  // Available games for assignment (not already assigned to a channel)
  const assignedGameIds = new Set(channels.filter(c => c.current_game_id).map(c => c.current_game_id));
  const availableGames = games.filter(g => !assignedGameIds.has(g.id));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-sky-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-channels">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Streaming Channels</h2>
          <p className="text-gray-400 text-sm mt-1">Persistent RTMP channels — assign to games, reuse the same stream key</p>
        </div>
        <Button variant="ghost" size="sm" onClick={loadChannels} className="text-gray-400 hover:text-white">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Create Channel */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardContent className="pt-4">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Label className="text-gray-400 text-xs">New Channel Name</Label>
              <Input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Channel 1, Field A Camera..."
                className="bg-gray-900 border-gray-700 text-white"
                data-testid="new-channel-name"
                onKeyDown={e => e.key === 'Enter' && createChannel()}
              />
            </div>
            <Button onClick={createChannel} disabled={creating} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="create-channel-btn">
              {creating ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              Create Channel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Channel List */}
      {channels.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Radio className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No channels yet. Create your first channel above.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {channels.map(ch => (
            <Card key={ch.id} className="bg-gray-800/50 border-gray-700 overflow-hidden" data-testid={`channel-${ch.id}`}>
              {/* Channel Header */}
              <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Radio className="w-5 h-5 text-sky-400" />
                  <span className="text-white font-semibold">{ch.name}</span>
                  <Badge className={`${STATUS_COLORS[ch.status] || 'bg-gray-500'} text-white text-xs`}>
                    {(ch.status || 'unknown').toUpperCase()}
                  </Badge>
                  {ch.scheduled_stop_time && (
                    <Badge className="bg-amber-600/50 text-amber-200 text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Stop: {new Date(ch.scheduled_stop_time).toLocaleString()}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => refreshStatus(ch.id)} className="text-gray-400 hover:text-white h-8 px-2">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteChannel(ch.id, ch.name)} className="text-red-400 hover:text-red-300 h-8 px-2" data-testid={`delete-channel-${ch.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <CardContent className="p-4 space-y-4">
                {/* RTMP Credentials */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-gray-400 text-xs">RTMP URL</Label>
                    <div className="flex items-center gap-1">
                      <code className="bg-black/40 text-green-400 px-3 py-1.5 rounded text-xs flex-1 font-mono truncate">{ch.rtmp_url}</code>
                      <Button variant="ghost" size="sm" onClick={() => copyText(ch.rtmp_url, `url-${ch.id}`)} className="h-8 px-2 text-gray-400 hover:text-white">
                        {copiedField === `url-${ch.id}` ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs">Stream Key (persistent)</Label>
                    <div className="flex items-center gap-1">
                      <code className="bg-black/40 text-amber-400 px-3 py-1.5 rounded text-xs flex-1 font-mono truncate">{ch.mux_stream_key || '—'}</code>
                      <Button variant="ghost" size="sm" onClick={() => ch.mux_stream_key && copyText(ch.mux_stream_key, `key-${ch.id}`)} className="h-8 px-2 text-gray-400 hover:text-white">
                        {copiedField === `key-${ch.id}` ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Current Assignment */}
                <div className="bg-black/30 rounded-lg p-3">
                  <Label className="text-gray-400 text-xs mb-2 block">Assigned Game</Label>
                  {ch.current_game ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-white text-sm font-medium">{ch.current_game.title}</span>
                        <span className="text-gray-500 text-xs ml-2">
                          {ch.current_game.scheduled_time ? new Date(ch.current_game.scheduled_time).toLocaleDateString() : ''}
                        </span>
                        <Badge className="ml-2 text-xs bg-gray-600 text-gray-200">
                          {ch.current_game.status}
                        </Badge>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => unassignChannel(ch.id)} className="text-orange-400 hover:text-orange-300 h-7 text-xs">
                        <Unlink className="w-3.5 h-3.5 mr-1" /> Unassign
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 text-sm italic">No game assigned</span>
                      <Button size="sm" onClick={() => { setAssignDialog({ channelId: ch.id, channelName: ch.name }); setSelectedGameId(''); }} className="bg-sky-600 hover:bg-sky-700 text-white h-7 text-xs" data-testid={`assign-btn-${ch.id}`}>
                        <Link2 className="w-3.5 h-3.5 mr-1" /> Assign Game
                      </Button>
                    </div>
                  )}
                </div>

                {/* Controls */}
                <div className="flex flex-wrap gap-2">
                  {ch.status === 'disabled' && (
                    <Button size="sm" onClick={() => enableChannel(ch.id)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`enable-${ch.id}`}>
                      <Play className="w-4 h-4 mr-1" /> Enable
                    </Button>
                  )}
                  {(ch.status === 'idle' || ch.status === 'active') && (
                    <Button size="sm" onClick={() => disableChannel(ch.id)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid={`disable-${ch.id}`}>
                      <Square className="w-4 h-4 mr-1" /> Stop
                    </Button>
                  )}
                  {(ch.status === 'idle' || ch.status === 'active') && !ch.scheduled_stop_time && (
                    <Button size="sm" variant="outline" onClick={() => { setScheduleDialog(ch.id); setScheduleTime(''); }} className="border-gray-600 text-gray-300 hover:bg-gray-700">
                      <Clock className="w-4 h-4 mr-1" /> Schedule Stop
                    </Button>
                  )}
                  {ch.scheduled_stop_time && (
                    <Button size="sm" variant="outline" onClick={() => cancelSchedule(ch.id)} className="border-amber-600 text-amber-300 hover:bg-amber-900/30">
                      <X className="w-4 h-4 mr-1" /> Cancel Schedule
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Assign Game Dialog */}
      <Dialog open={!!assignDialog} onOpenChange={() => setAssignDialog(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Game to {assignDialog?.channelName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-gray-400 text-sm">Select Game</Label>
              <Select value={selectedGameId} onValueChange={setSelectedGameId}>
                <SelectTrigger className="bg-gray-800 border-gray-700 text-white mt-1" data-testid="assign-game-select">
                  <SelectValue placeholder="Choose a game..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  {availableGames.length === 0 ? (
                    <div className="text-gray-500 text-sm p-3">No unassigned games available</div>
                  ) : (
                    availableGames.map(g => (
                      <SelectItem key={g.id} value={g.id} className="text-white">
                        {g.home_team_name || 'TBD'} vs {g.away_team_name || 'TBD'}
                        <span className="text-gray-500 text-xs ml-2">
                          {g.scheduled_time ? new Date(g.scheduled_time).toLocaleDateString() : ''}
                        </span>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setAssignDialog(null)} className="text-gray-400">Cancel</Button>
              <Button onClick={handleAssign} disabled={!selectedGameId} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="confirm-assign-btn">
                Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Conflict Dialog */}
      <Dialog open={!!conflictDialog} onOpenChange={() => setConflictDialog(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="w-5 h-5" /> Channel Conflict
            </DialogTitle>
          </DialogHeader>
          {conflictDialog && (
            <div className="space-y-4 pt-2">
              <p className="text-gray-300 text-sm">
                This channel is currently assigned to:
              </p>
              <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-3">
                <p className="text-white font-medium">{conflictDialog.conflict.title}</p>
                <p className="text-gray-400 text-xs mt-1">
                  Status: <Badge className="bg-gray-600 text-xs">{conflictDialog.conflict.status}</Badge>
                  {conflictDialog.conflict.scheduled_time && (
                    <span className="ml-2">{new Date(conflictDialog.conflict.scheduled_time).toLocaleString()}</span>
                  )}
                </p>
              </div>
              <p className="text-gray-400 text-sm">
                Reassigning will mark the current game as <strong className="text-white">finished</strong> and move the channel to the new game.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setConflictDialog(null)} className="text-gray-400">Cancel</Button>
                <Button onClick={handleForceAssign} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="force-assign-btn">
                  Reassign Channel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule Stop Dialog */}
      <Dialog open={!!scheduleDialog} onOpenChange={() => setScheduleDialog(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Schedule Auto-Stop</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-gray-400 text-sm">Stop At</Label>
              <Input
                type="datetime-local"
                value={scheduleTime}
                onChange={e => setScheduleTime(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white mt-1"
                data-testid="schedule-stop-time"
              />
              <p className="text-gray-500 text-xs mt-1">The channel will auto-stop and the game will be marked as finished at this time.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setScheduleDialog(null)} className="text-gray-400">Cancel</Button>
              <Button onClick={handleScheduleStop} disabled={!scheduleTime} className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="confirm-schedule-btn">
                Schedule
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminChannels;
